import type { ApiResponse } from "@energyos/shared-types";
import { useAuthStore } from "../stores/auth.store.js";

const BASE_URL = "/api";

/** localStorage-Key des persistierten Auth-Stores (zustand/persist, auth.store.ts). */
export const AUTH_STORAGE_KEY = "energyos-auth";

/** Ziel nach erkannter abgelaufener Session — LoginPage zeigt dazu die Meldung an. */
export const SESSION_EXPIRED_REDIRECT = "/login?reason=expired";

function getToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { state?: { user?: { token?: string } } };
    return parsed.state?.user?.token ?? null;
  } catch {
    return null;
  }
}

/**
 * Erkennt eine abgelaufene/ungültige Session an Status oder Fehler-Envelope.
 * 403 ist ausgenommen: require-role.ts antwortet 403 + "permission_error" für
 * fehlende Rollen-Rechte — das ist KEINE abgelaufene Session und darf nicht ausloggen.
 */
export function isSessionExpiredResponse(status: number, body: unknown): boolean {
  if (status === 401) return true;
  if (status === 403) return false;
  if (!body || typeof body !== "object") return false;
  const envelope = body as { success?: unknown; code?: unknown; error?: { code?: unknown } };
  if (envelope.success !== false) return false;
  return envelope.error?.code === "permission_error" || envelope.code === "permission_error";
}

function isOnLoginRoute(pathname: string): boolean {
  return pathname === "/login" || pathname.startsWith("/login/");
}

/**
 * Abgelaufene Session zentral behandeln: Auth-Store + persistierten State leeren und
 * mit vollem Page-Load nach /login?reason=expired (setzt alle Stores/Caches zurück).
 * Auf /login selbst passiert nichts — ein fehlgeschlagener Login-Versuch (ebenfalls
 * 401 permission_error) darf keinen Redirect-Loop auslösen.
 */
export function handleSessionExpiry(): boolean {
  if (typeof window === "undefined") return false;
  if (isOnLoginRoute(window.location.pathname)) return false;

  try {
    useAuthStore.getState().logout();
  } catch {
    // Store nicht initialisiert — localStorage-Cleanup unten reicht.
  }
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Storage nicht verfügbar (z. B. Privacy-Modus) — Redirect genügt.
  }

  window.location.assign(SESSION_EXPIRED_REDIRECT);
  return true;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  const token = getToken();
  // Content-Type nur setzen, wenn ein Body mitgeht. Sonst lehnt Fastify
  // bodylose Requests (z. B. DELETE) mit FST_ERR_CTP_EMPTY_JSON_BODY (400) ab.
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    // Netzwerk-Ebene (offline / DNS / CORS): fetch lehnt ab, statt zu antworten.
    // Statt die Promise abzulehnen (Aufrufer hängen) ein Fehler-Envelope zurückgeben.
    return {
      success: false,
      error: {
        code: "network_error",
        message: "Netzwerkfehler — bitte Verbindung prüfen.",
      },
    };
  }

  // 204 No Content (z. B. erfolgreiche DELETE) hat keinen Body — res.json()
  // würde werfen und der Aufrufer würde einen Erfolg fälschlich als Fehler werten.
  if (res.status === 204) {
    return { success: true, data: undefined as T };
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    // Nicht-JSON (z. B. Proxy-Fehlerseite): als Fehler-Envelope zurückgeben statt werfen.
    payload = {
      success: false,
      error: {
        code: res.status === 401 ? "permission_error" : "unknown_error",
        message: `Unerwartete Antwort vom Server (HTTP ${res.status}).`,
      },
    };
  }

  if (isSessionExpiredResponse(res.status, payload)) {
    handleSessionExpiry();
  }

  return payload;
}

export const apiClient = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
